import { USER_ROLES } from "./session";

const freezeList = (items) => Object.freeze([...items]);

export const APP_ROUTES = Object.freeze({
  LOGIN: "/",
  MENU: "/Menu",
  INSTITUTIONS: "/Instituicoes",
  CLASSES: "/Turmas",
  STUDENTS: "/AlunoInt",
  PUBLIC_STUDENT_REGISTRATION: "/CadAlunoExt/:token",
  STUDENT_APPROVALS: "/CadAprovAlunoExt",
  EVENT_REGISTRATION: "/CadEvento",
  PHOTOGRAPHERS: "/Fotografos",
  PRODUCTS: "/Produtos",
  SALES: "/Vendas",
  SALE_DETAIL: "/Vendas/:id",
  EVENTS: "/Eventos",
  EVENT_DETAIL: "/Evento/Detalhe/:id",
  UPLOAD: "/Upload",
  IMAGE_POLICY: "/Configuracoes/Imagens",
  RECOGNITION_OPERATIONS: "/Recognition",
  PUBLICATION_OPERATIONS: "/Publicacao",
  STUDENT_PROFILE: "/Aluno/Perfil/:id",
  STUDENT_AREA: "/AreaAluno",
  PHOTO_SELECTION: "/SelecaoFotos",
});

export const ROUTE_ROLE_ACCESS = Object.freeze({
  MENU: freezeList([USER_ROLES.ADMIN, USER_ROLES.PHOTOGRAPHER]),
  INSTITUTIONS: freezeList([USER_ROLES.ADMIN]),
  CLASSES: freezeList([USER_ROLES.ADMIN]),
  STUDENTS: freezeList([USER_ROLES.ADMIN]),
  STUDENT_APPROVALS: freezeList([USER_ROLES.ADMIN]),
  EVENT_REGISTRATION: freezeList([USER_ROLES.ADMIN]),
  PHOTOGRAPHERS: freezeList([USER_ROLES.ADMIN]),
  PRODUCTS: freezeList([USER_ROLES.ADMIN]),
  SALES: freezeList([USER_ROLES.ADMIN]),
  SALE_DETAIL: freezeList([USER_ROLES.ADMIN]),
  EVENTS: freezeList([USER_ROLES.ADMIN, USER_ROLES.PHOTOGRAPHER]),
  EVENT_DETAIL: freezeList([USER_ROLES.ADMIN, USER_ROLES.PHOTOGRAPHER]),
  UPLOAD: freezeList([USER_ROLES.ADMIN, USER_ROLES.PHOTOGRAPHER]),
  IMAGE_POLICY: freezeList([USER_ROLES.ADMIN]),
  RECOGNITION_OPERATIONS: freezeList([USER_ROLES.ADMIN]),
  PUBLICATION_OPERATIONS: freezeList([USER_ROLES.ADMIN]),
  STUDENT_PROFILE: freezeList([USER_ROLES.ADMIN]),
  STUDENT_AREA: freezeList([USER_ROLES.USER]),
  PHOTO_SELECTION: freezeList([USER_ROLES.USER, USER_ROLES.ADMIN]),
});

export const OPERATIONAL_CAPABILITIES = Object.freeze({
  SETTINGS: "SETTINGS",
  RECOGNITION_CALIBRATION: "RECOGNITION_CALIBRATION",
  LOCAL_STORAGE_CAPACITY: "LOCAL_STORAGE_CAPACITY",
});

export const CAPABILITY_ROLE_ACCESS = Object.freeze({
  [OPERATIONAL_CAPABILITIES.SETTINGS]: freezeList([USER_ROLES.ADMIN]),
  [OPERATIONAL_CAPABILITIES.RECOGNITION_CALIBRATION]: freezeList([
    USER_ROLES.ADMIN,
  ]),
  [OPERATIONAL_CAPABILITIES.LOCAL_STORAGE_CAPACITY]: freezeList([
    USER_ROLES.ADMIN,
  ]),
});

const NAVIGATION_BY_ROLE = Object.freeze({
  [USER_ROLES.ADMIN]: freezeList([
    { id: "dashboard", label: "Dashboard", path: APP_ROUTES.MENU, icon: "dashboard" },
    {
      id: "registrations",
      label: "Cadastros",
      icon: "folder",
      children: freezeList([
        { id: "institutions", label: "Instituições", path: APP_ROUTES.INSTITUTIONS, icon: "school" },
        { id: "classes", label: "Turmas", path: APP_ROUTES.CLASSES, icon: "users" },
        { id: "students", label: "Alunos", path: APP_ROUTES.STUDENTS, icon: "student" },
        { id: "student-approvals", label: "Aprovar/Reprovar", path: APP_ROUTES.STUDENT_APPROVALS, icon: "student" },
        { id: "event-registration", label: "Eventos", path: APP_ROUTES.EVENT_REGISTRATION, icon: "calendar" },
        { id: "photographers", label: "Fotógrafos", path: APP_ROUTES.PHOTOGRAPHERS, icon: "id-card" },
        { id: "products", label: "Produtos", path: APP_ROUTES.PRODUCTS, icon: "products" },
      ]),
    },
    { id: "events", label: "Eventos", path: APP_ROUTES.EVENTS, icon: "calendar" },
    { id: "upload", label: "Upload", path: APP_ROUTES.UPLOAD, icon: "upload" },
    {
      id: "publication-operations",
      label: "Publicação de fotos",
      path: APP_ROUTES.PUBLICATION_OPERATIONS,
      icon: "images",
    },
    {
      id: "recognition-operations",
      label: "Análise facial",
      path: APP_ROUTES.RECOGNITION_OPERATIONS,
      icon: "face-analysis",
    },
    {
      id: "sales",
      label: "Pedidos e vendas",
      path: APP_ROUTES.SALES,
      icon: "sales",
    },
    {
      id: "image-policy",
      label: "Políticas e Configurações",
      path: APP_ROUTES.IMAGE_POLICY,
      icon: "settings",
    },
  ]),
  [USER_ROLES.PHOTOGRAPHER]: freezeList([
    { id: "dashboard", label: "Dashboard", path: APP_ROUTES.MENU, icon: "dashboard" },
    { id: "events", label: "Eventos", path: APP_ROUTES.EVENTS, icon: "calendar" },
    { id: "upload", label: "Upload", path: APP_ROUTES.UPLOAD, icon: "upload" },
  ]),
  [USER_ROLES.USER]: freezeList([
    { id: "student-area", label: "Área do aluno", path: APP_ROUTES.STUDENT_AREA, icon: "id-card" },
    { id: "photo-selection", label: "Seleção de fotos", path: APP_ROUTES.PHOTO_SELECTION, icon: "images" },
  ]),
});

const DASHBOARD_BY_ROLE = Object.freeze({
  [USER_ROLES.ADMIN]: Object.freeze({
    title: "Visão geral do sistema",
    subtitle: "Resumo administrativo da sua empresa.",
    cards: freezeList([
      { title: "Instituições", field: "institutions" },
      { title: "Turmas", field: "classes" },
      { title: "Alunos", field: "students" },
      { title: "Eventos", field: "events" },
      { title: "Fotos", field: "photos" },
    ]),
    showOperationalIndicators: true,
    showMonthlyIndicators: true,
  }),
  [USER_ROLES.PHOTOGRAPHER]: Object.freeze({
    title: "Sua operação fotográfica",
    subtitle: "Acompanhe apenas os eventos e fotos vinculados ao seu perfil.",
    cards: freezeList([
      { title: "Eventos vinculados", field: "events" },
      { title: "Fotos no seu escopo", field: "photos" },
    ]),
    showOperationalIndicators: false,
    showMonthlyIndicators: false,
    emptyMessage: "Nenhum evento está vinculado ao seu perfil no momento.",
  }),
  [USER_ROLES.USER]: Object.freeze({
    title: "Área do aluno",
    subtitle: "Acesse somente seu perfil e os conteúdos autorizados.",
    cards: freezeList([]),
    showOperationalIndicators: false,
    showMonthlyIndicators: false,
  }),
});

export const getAllowedRoles = (routeKey) =>
  ROUTE_ROLE_ACCESS[routeKey] || freezeList([]);

export const getNavigationItems = (role) =>
  NAVIGATION_BY_ROLE[role] || freezeList([]);

export const getDashboardDefinition = (role) =>
  DASHBOARD_BY_ROLE[role] || null;

export const canUseCapability = (role, capability) =>
  (CAPABILITY_ROLE_ACCESS[capability] || []).includes(role);
